/**
 * DeathSnapshotStore — capture-before-removal seam for death presentation.
 *
 * K25 (docs/plans/fx-grammar-specs/death-and-kill-beats.md §2.1): the two
 * client death signals — the `npcDefeated` direct message and the
 * `npcs.onRemove` schema patch — race on the wire, and the entity is deleted
 * the same tick it dies. Anything that wants to draw at the corpse therefore
 * needs a snapshot captured while the entity still existed. This store
 * generalizes RemoteMobManager's old `lastSeenMobInfo` (the respawn-nametag
 * anchor precedent), which now reads from here too.
 *
 * Entries self-prune after ENTRY_TTL_MS on read/write (the ActiveStatusIndex
 * prune-on-read pattern — no timers to leak).
 */

export interface DeathSnapshot {
  x: number;
  y: number;
  width?: number;
  height?: number;
  facing?: string;
  /** SpriteTextureSystem cache key — seam for GhostSpriteDissolve (spec §1.1). */
  textureKey?: string;
  /** Boss/elite discriminator — same source the old live-mob lookup used
   *  (`mob.getData('mobType')`), captured before removal so the boss finisher
   *  no longer races the state deletion (spec §0.5). */
  mobType?: string;
  mobName?: string;
  xpValue?: number;
  /** Killing-blow school thread (spec §2.1 write-site 1) — written by the
   *  DamagePresentationService slice; stays undefined in the K25 slice. */
  lastSchool?: string;
  lastAbilityId?: string;
  lastSchoolAt?: number;
  updatedAt: number;
}

const ENTRY_TTL_MS = 10_000;

const entries = new Map<string, DeathSnapshot>();

function prune(now: number): void {
  for (const [id, snap] of entries) {
    if (now - snap.updatedAt > ENTRY_TTL_MS) entries.delete(id);
  }
}

export const DeathSnapshotStore = {
  /** Merge-write a snapshot for an entity — partial patches keep prior fields
   *  (so a school write and a position write compose). */
  write(id: string, patch: Omit<Partial<DeathSnapshot>, 'updatedAt'>): void {
    const now = Date.now();
    prune(now);
    const prior = entries.get(id);
    entries.set(id, { x: 0, y: 0, ...prior, ...patch, updatedAt: now });
  },

  get(id: string): DeathSnapshot | undefined {
    prune(Date.now());
    return entries.get(id);
  },

  has(id: string): boolean {
    prune(Date.now());
    return entries.has(id);
  },

  remove(id: string): void {
    entries.delete(id);
  },

  clear(): void {
    entries.clear();
  },

  size(): number {
    prune(Date.now());
    return entries.size;
  },
};
